import { faLocationArrow } from '@fortawesome/free-solid-svg-icons/faLocationArrow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dropdown,
  FlexGrid,
  Form,
  Header,
  Input,
  Label,
  Text,
  Visibility,
} from '@gasbuddy/react-components';
import { useDataLayer, useWindowSize } from '@gasbuddy/react-hooks';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRY_CODES, FUEL_OPTIONS, MAX_PRICE_AGE_OPTIONS, PAYMENT_OPTIONS } from '../../constants';
import BrandsDropdown from '../BrandsDropdown';
import styles from './GasPriceSearchForm.module.css';

const cx = classnames.bind(styles);

const NEAR_ME_SIGNIFYING_STRING = 'Current Location';

export default function GasPriceSearchForm({
  brand,
  country,
  fuel,
  onSubmit,
  maxAge,
  method,
  search,
}) {
  const { mobile: isOnMobileDevice } = useWindowSize();
  const { pushToDataLayer } = useDataLayer();
  const [expandedSearchVisible, setExpandedSearchVisible] = useState(true);
  const [isGeoLocating, setIsGeoLocating] = useState(false);
  const [searchTermError, setSearchTermError] = useState(undefined);
  const [searchValue, setSearchValue] = useState(search || '');
  const [fuelValue, setFuelValue] = useState(fuel || 1);
  const [brandIdValue, setBrandIdValue] = useState(brand || 1);
  const [maxAgeValue, setMaxAgeValue] = useState(maxAge || 0);
  const [methodValue, setMethodValue] = useState(method || 'all');
  const { t } = useTranslation(['station', 'location', 'fuel', 'brand']);

  const formValues = useMemo(() => ({
    search: searchValue,
    fuel: fuelValue,
    method: methodValue,
    brandId: brandIdValue,
    maxAge: maxAgeValue,
  }), [brandIdValue, fuelValue, maxAgeValue, methodValue, searchValue]);

  const handleLocationIconClick = useCallback(() => {
    setSearchValue(NEAR_ME_SIGNIFYING_STRING);
    setSearchTermError(undefined);
  }, []);

  const handleSearchToggle = useCallback(() => {
    // NOTE: we don't want to reset the brandId and maxAge values here in case the user re-toggles
    setExpandedSearchVisible(!expandedSearchVisible);
  }, [expandedSearchVisible]);

  const handleFieldChange = useCallback((e) => {
    const { name, value } = e.target;

    switch (name) {
      case 'search':
        setSearchValue(value);
        setSearchTermError(undefined);
        break;
      case 'fuel':
        setFuelValue(parseInt(value, 10));
        break;
      case 'method':
        setMethodValue(value);
        break;
      case 'brandId':
        setBrandIdValue(parseInt(value, 10));
        break;
      case 'maxAge':
        setMaxAgeValue(parseInt(value, 10));
        break;
      default:
    }
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const submittedValues = { ...formValues };

    if (!submittedValues.search) {
      setSearchTermError(t('search.noSearchTerm', { ns: 'station' }));
      return;
    }

    if (!expandedSearchVisible) {
      // clear out brandId and maxAge in the values we submit if we have a minimized search
      submittedValues.brandId = undefined;
      submittedValues.maxAge = undefined;
    } else if (parseInt(submittedValues.brandId, 10) === 1) {
      // a brandId of 1 signifies an All Stations search, so we clear it out to signify all brands
      submittedValues.brandId = undefined;
    }

    // Check if we're doing a near me search. If so, perform it.
    if (submittedValues.search.toLowerCase() === NEAR_ME_SIGNIFYING_STRING.toLowerCase()) {
      if ('geolocation' in window.navigator) {
        setIsGeoLocating(true);
        window.navigator.geolocation.getCurrentPosition((position) => {
          const { coords } = position;
          setIsGeoLocating(false);

          // Push search_station event to GTM data layer
          pushToDataLayer({
            event: 'search_station',
            search_term: NEAR_ME_SIGNIFYING_STRING,
          }, true);

          onSubmit({
            ...submittedValues,
            search: undefined,
            lat: coords.latitude,
            lng: coords.longitude,
          });
        }, (error) => {
          if (error.code === 3) { // Timeout expired
            setSearchTermError(t('problemGettingLocationCheckPrefs', { ns: 'location' }));
          } else {
            setSearchTermError(t('problemGettingLocationGeneric', { ns: 'location' }));
          }
          setIsGeoLocating(false);
        }, {
          timeout: 10000,
        });
      } else {
        setSearchTermError(t('noLocationAvailable', { ns: 'location' }));
      }
      return;
    }

    // Push search_station event to GTM data layer
    pushToDataLayer({
      event: 'search_station',
      search_term: submittedValues.search,
    }, true);

    onSubmit(submittedValues);
  }, [expandedSearchVisible, formValues, onSubmit, pushToDataLayer, t]);

  // ensure component renders with basic search when rendered on mobile or tablet
  useEffect(() => {
    if (isOnMobileDevice) {
      setExpandedSearchVisible(false);
    }
  }, [isOnMobileDevice]);

  // ensure advanced search is visible if brand or maxAge is set
  useEffect(() => {
    if (!!brand || maxAge >= 0) {
      setExpandedSearchVisible(true);
    }
  }, [brand, maxAge]);

  const fuelOptions = useMemo(() => FUEL_OPTIONS.map(option => ({
    ...option,
    label: t(`${option.label.toLowerCase()}Fuel`, { ns: 'fuel' }),
  })), [t]);

  const paymentOptions = useMemo(() => PAYMENT_OPTIONS.map(option => ({
    ...option,
    label: t(`search.paymentOption_${option.key}`, { ns: 'station' }),
  })), [t]);

  const ageOptions = useMemo(() => MAX_PRICE_AGE_OPTIONS.map(option => ({
    ...option,
    label: t('search.priceAgeOption', { ns: 'station', count: option.value }),
  })), [t]);

  return (
    <Fragment>
      <FlexGrid className={cx('headerGrid')}>
        <FlexGrid.Column mobile={8}>
          <Header as="h1" className={cx('header')} color="midnight">
            {t('search.searchGasPrices', { ns: 'station' })}
          </Header>
        </FlexGrid.Column>
        <FlexGrid.Column className={cx('searchToggleColumn')} mobile={4}>
          {isOnMobileDevice && (
            <Button
              className={cx('searchToggleButton')}
              link
              onClick={handleSearchToggle}
            >
              {expandedSearchVisible
                ? t('search.hideFilters', { ns: 'station' })
                : t('search.showFilters', { ns: 'station' })}
            </Button>
          )}
        </FlexGrid.Column>
      </FlexGrid>
      <Form
        method="GET"
        action="/home" // Specifying action so that smartphone keyboard shows "Search" instead of "return"
        onSubmit={handleSubmit}
        role="search"
      >
        <FlexGrid container className={cx('gasPriceSearchFormRow')}>
          <FlexGrid.Column className={cx('searchTermContainer')}>
            <Input
              id="search"
              name="search"
              type="search"
              data-testid="search"
              label={t(`search.searchInputLabel${country}`, { ns: 'station' })}
              onChange={handleFieldChange}
              value={searchValue}
              className={cx('searchFormControl')}
              innerComponent={(
                <FontAwesomeIcon
                  icon={faLocationArrow}
                  className={cx('nearMeIcon')}
                  onClick={handleLocationIconClick}
                />
              )}
            />
            {searchTermError && (
              <Text className={cx('searchTermError')} color="orange">{searchTermError}</Text>
            )}
          </FlexGrid.Column>
          <FlexGrid.Column tablet={6}>
            <Label htmlFor="searchFuelType">
              {t('fuelType', { ns: 'fuel' })}
            </Label>
            <Dropdown
              id="searchFuelType"
              name="fuel"
              onChange={handleFieldChange}
              options={fuelOptions}
              defaultValue={fuelValue}
              className={cx('searchFormControl')}
            />
          </FlexGrid.Column>
          <FlexGrid.Column tablet={6}>
            <Label htmlFor="searchPaymentMethod">
              {t('search.paymentMethod', { ns: 'station' })}
            </Label>
            <Dropdown
              id="searchPaymentMethod"
              name="method"
              onChange={handleFieldChange}
              options={paymentOptions}
              defaultValue={methodValue}
              className={cx('searchFormControl')}
            />
          </FlexGrid.Column>
        </FlexGrid>
        <Visibility mobile={expandedSearchVisible}>
          <FlexGrid container className={cx('gasPriceSearchFormRow')}>
            <FlexGrid.Column desktop={6}>
              <Label htmlFor="searchBrandId">
                {t('stationBrand', { ns: 'brand' })}
              </Label>
              <BrandsDropdown
                id="searchBrandId"
                label=""
                name="brandId"
                onChange={handleFieldChange}
                selectedBrandId={brandIdValue}
                showAllBrandsOption
                className={cx('searchFormControl')}
              />
            </FlexGrid.Column>
            <FlexGrid.Column desktop={6}>
              <Label htmlFor="maxAge">
                {t('search.priceUpdatedInLast', { ns: 'station' })}
              </Label>
              <Dropdown
                id="maxAge"
                name="maxAge"
                onChange={handleFieldChange}
                options={ageOptions}
                defaultValue={maxAgeValue}
                className={cx('searchFormControl')}
              />
            </FlexGrid.Column>
          </FlexGrid>
        </Visibility>
        <Button
          type="submit"
          className={cx('submitButton')}
          primary
          fluid
          loading={isGeoLocating}
        >
          {t('search.findGas', { ns: 'station' })}
        </Button>
      </Form>
    </Fragment>
  );
}

GasPriceSearchForm.propTypes = {
  brand: PropTypes.number,
  country: PropTypes.oneOf(Object.values(COUNTRY_CODES)),
  fuel: PropTypes.number,
  onSubmit: PropTypes.func,
  maxAge: PropTypes.number,
  search: PropTypes.string,
  method: PropTypes.oneOf(['', 'credit', 'all']),
};

GasPriceSearchForm.defaultProps = {
  brand: undefined,
  country: COUNTRY_CODES.USA,
  fuel: undefined,
  onSubmit: () => { },
  maxAge: undefined,
  search: undefined,
  method: undefined,
};
